import React, { type FC, Fragment } from "react";
import { shallowEqual } from "react-redux";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { useCarConfigSelector, useCarConfigDispatch } from "../../../../../redux/store";
import { formatTaxLocations, tfrTaxLocations } from "../../../../../../../../common-deprecated/utils/taxLocations";
import { setExternalPriceTaxLocation } from "../../../../../redux/slices/carConfigExternalPriceSlice";
import { taxLabelsSelector } from "../../../../../utils/selectorUtils";
import SelectList from "../../../../../../shared-components/css-modules/toyota/molecules/Selectlist";
import styles from "./Summary.module.scss";
import Popover from "../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import useAphExternalPriceTaxDisclaimer from "../../../../../hooks/useAphExternalPriceTaxDisclaimer";

type TaxAndCostsProps = {
    variant: {
        registrationTax?: boolean; // Registration tax, currently only used for TFR.
        wltpTax?: boolean; // WLTP tax section, e.g. BPM or VED taxes.
    };
};

const SummaryTaxAndCosts: FC<TaxAndCostsProps> = ({ variant: { registrationTax = false, wltpTax = false } }) => {
    const dispatch = useCarConfigDispatch();
    const country = useCarConfigSelector((state) => state.commonSettings.country);

    const { co2TaxLabels, fullTaxLabel, co2Disclaimer, formattedTax } = useAphExternalPriceTaxDisclaimer();

    const taxLocations = formatTaxLocations(tfrTaxLocations) || [];

    const currentTaxLocation = useCarConfigSelector((state) =>
        taxLocations.find((location) => location.id === state.externalPrice.taxLocationId),
    );
    const taxLabels = useCarConfigSelector(taxLabelsSelector, shallowEqual);

    const [costsAndTaxesSelectTitleLabel, costsAndTaxesSearchLabel] = useCarConfigLabel([
        "carConfigCostsAndTaxesSelectTitle",
        "carConfigCostsAndTaxesSearch",
    ]);

    const selectOptions = taxLocations.map((location, index) => {
        return {
            id: index,
            label: location.label,
            selected: location.id === currentTaxLocation?.id,
            onClick: () => {
                dispatch(setExternalPriceTaxLocation({ locationId: location.id }));
            },
        };
    });

    return (
        <div className={styles["taxinfo-wrapper"]}>
            <Text tag="h2" variant="apheleia-h4" spacing="spacing-1">
                <CarConfigLabel label="carConfigCostsAndTaxes" />
            </Text>

            <div className={styles.taxinfo}>
                {wltpTax && (
                    <div className={styles.taxrow}>
                        <Text tag="h4" variant="apheleia-body">
                            {co2TaxLabels ? `${co2TaxLabels.start} ${co2TaxLabels.value}` : fullTaxLabel}
                            <Popover placement="top">
                                <Popover.Trigger>
                                    <Button
                                        variant="clean"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        <Icon variant="info" sizing="md" aria-hidden="true" />
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content>{co2Disclaimer}</Popover.Content>
                            </Popover>
                        </Text>

                        <Text tag="h4" variant="apheleia-body">
                            <span dangerouslySetInnerHTML={{ __html: formattedTax }} />
                        </Text>
                    </div>
                )}
                {registrationTax && (
                    <>
                        <Text variant="apheleia-body" spacing="spacing-1">
                            <CarConfigLabel label="carConfigCostsAndTaxesDescription" />
                        </Text>
                        {/* Only show the tax selector for France */}
                        {country === "fr" && (
                            <SelectList
                                label={currentTaxLocation?.label || costsAndTaxesSelectTitleLabel}
                                placeholder={costsAndTaxesSearchLabel}
                                options={selectOptions}
                            />
                        )}
                        <div>
                            {!!taxLabels &&
                                taxLabels.map((tax) => (
                                    <div className={styles.taxrow} key={tax.key}>
                                        <Text tag="div" variant="apheleia-body">
                                            {tax.label}
                                            {tax.disclaimer && (
                                                <Popover placement="top">
                                                    <Popover.Trigger>
                                                        <Button
                                                            variant="clean"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                            }}
                                                        >
                                                            <Icon variant="info" sizing="md" aria-hidden="true" />
                                                            <span className={a11y["sr-only"]}>
                                                                <CarConfigLabel label="carConfigA11yButtonMoreInfoAbout" />
                                                                {tax.label}
                                                            </span>
                                                        </Button>
                                                    </Popover.Trigger>
                                                    <Popover.Content>{tax.disclaimer}</Popover.Content>
                                                </Popover>
                                            )}
                                        </Text>
                                        <Text tag="div" variant="apheleia-body">
                                            {!!tax.prices.length &&
                                                tax.prices.map((price) => (
                                                    <Fragment key={`${tax.key}-price`}>{price}</Fragment>
                                                ))}
                                        </Text>
                                    </div>
                                ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SummaryTaxAndCosts;
